import { useEffect, useMemo, useRef, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Race Condition (level 55) — the Precision rung. You do not click the window: you name the instants, in
// milliseconds, and the server runs the chain.
//
// There is no ticker in here and nothing anchored to the wall clock: the board is a static timeline and
// the answer is a list of numbers. Grading on when a click ARRIVES would make network jitter the
// difficulty, and letting the browser stamp its own instant would hand it the outcome.
//
// THE LINE THIS VIEW DRAWS: it renders the published schedules and never the verdict. It does not say
// whether a stage sits inside an alignment, because that is the question. What it will show is arithmetic
// on the PLAYER'S OWN choices — the gap between two stages they picked, against a published band — since
// restating your own input is not answering anything.
//
// It also does not draw a "where a write fits" strip. That strip was the answer, the game became clicking
// the green, and it is why the rung is a chain now: with one instant to find, any drawing of the schedules
// solves it.

interface Watcher {
  name: string;
  periodMs: number;
  startMs: number;
  lenMs: number;
}

interface Attempt {
  ats: number[];
  notes: string[];
  ok: boolean;
}

interface Challenge {
  started: boolean;
  spanMs: number;
  writeMs: number;
  processCount: number;
  stages: number;
  recoverMs: number;
  patienceMs: number;
  watchers: Watcher[];
  strikesLeft: number;
  strikesGranted: number;
  history: Attempt[];
  timeLimitSec: number;
  readingAllowanceSec: number;
}

export default function RaceConditionView({
  challenge, onAct, onSubmit, setPendingSubmission,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [ats, setAts] = useState<number[]>([]);
  const [editing, setEditing] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const sendingRef = useRef(false);

  const span = live?.spanMs ?? 4000;
  const write = live?.writeMs ?? 24;
  const stages = live?.stages ?? 0;
  const watchers = useMemo(() => live?.watchers ?? [], [live]);
  const history = useMemo(() => live?.history ?? [], [live]);
  const started = live?.started ?? false;

  useEffect(() => {
    setLive(challenge ?? null);
    setAts([]);
    setEditing(0);
    setNote(null);
    sendingRef.current = false;
  }, [challenge]);

  // Lay the stages out spread across the timeline the first time they are known, so the player starts by
  // moving marks rather than by conjuring numbers.
  useEffect(() => {
    if (stages > 0 && ats.length !== stages) {
      setAts(Array.from({ length: stages }, (_, i) =>
        Math.round(((i + 1) * (span - write)) / (stages + 1))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages, span, write]);

  useEffect(() => { setPendingSubmission({ ats }); }, [ats, setPendingSubmission]);

  const send = async (action: unknown) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setNote(null);
    try {
      const next = await onAct(action);
      if (next) setLive(next);
    } finally {
      sendingRef.current = false;
    }
  };

  const row: React.CSSProperties = { fontFamily: 'monospace', fontSize: '0.8rem' };
  const pct = (ms: number) => `${(ms / span) * 100}%`;

  const at = ats[editing] ?? 0;
  const setAt = (v: number) => setAts(a => a.map((x, i) =>
    i === editing ? Math.max(0, Math.min(span - write, v)) : x));

  // The magnifier's slice, centred on the stage being placed.
  const ZOOM_MS = 240;
  const z0 = Math.max(0, Math.min(span - ZOOM_MS, at + write / 2 - ZOOM_MS / 2));
  const zpct = (ms: number) => `${((ms - z0) / ZOOM_MS) * 100}%`;

  const windowsIn = (w: Watcher, from: number, to: number) => {
    const out: { from: number; to: number }[] = [];
    for (let k = Math.floor(from / w.periodMs); k <= Math.ceil(to / w.periodMs); k++) {
      const a = k * w.periodMs + w.startMs;
      const b = a + w.lenMs;
      if (b <= from || a >= to) continue;
      out.push({ from: Math.max(a, from), to: Math.min(b, to) });
    }
    return out;
  };

  if (!started) {
    return (
      <div style={{ fontFamily: 'monospace' }}>
        <div style={{ border: '1px solid #232833', borderRadius: 4, padding: '0.9rem', background: '#0d1117' }}>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            A resource is guarded by <strong>{live?.processCount ?? 3} processes</strong>. Each checks it,
            does something else for a while, then uses what it checked — and the gap between the check and
            the use is the moment it is <strong>blind</strong>. Their periods differ, so those gaps drift in
            and out of line with each other.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            A write needs <strong>{live?.writeMs ?? 24} ms</strong> and only slips through when it sits
            entirely inside <em>every</em> blind gap at once. And one write is not enough: the exploit takes
            a <strong>chain of {live?.stages ?? 3} stages</strong>.
          </p>
          <p style={{ ...row, color: '#60a5fa', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            The chain is where the work is. Consecutive stages must be{' '}
            <strong>{live?.recoverMs ?? 300}–{live?.patienceMs ?? 620} ms</strong> apart — closer and the
            tool has not re-armed, further and the half-open state has lapsed. So it is not enough to find
            openings: you have to find a <em>run of them</em> whose spacings all work, and taking them in
            the order they happen to appear will not do it.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem' }}>
            You get <strong>{live?.strikesGranted ?? 3} attempt{(live?.strikesGranted ?? 3) === 1 ? '' : 's'}</strong>
            {' '}at the whole chain. A failed one tells you, stage by stage, what went wrong.
            {(live?.writeMs ?? 24) > 24
              ? ' Your SshExploit is below the version this needs, so each write is slow and wants a wider opening.'
              : ' A better SshExploit writes faster, and a faster write fits through a tighter opening.'}
          </p>
          <p style={{ ...row, color: 'var(--text-muted)', margin: '0 0 0.8rem' }}>
            The schedules arrive when you attach the debugger. The clock is already running — about{' '}
            {live?.readingAllowanceSec ?? 30}s of it is set aside for reading this.
          </p>
          <button
            className="primary"
            onClick={() => void send({ kind: 'begin' })}
            style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}
          >
            Attach the debugger
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'monospace' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.9rem', flexWrap: 'wrap',
        border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.5rem 0.7rem',
        marginBottom: '0.6rem', background: '#0d1117',
      }}>
        <span style={{ ...row, fontSize: '0.74rem', minWidth: '8rem', color: 'var(--text-muted)' }}>
          write {write} ms · gaps {live?.recoverMs}–{live?.patienceMs}
        </span>
        <span style={{ ...row, fontSize: '0.74rem', minWidth: '7rem', color: (live?.strikesLeft ?? 0) > 0 ? '#cbd5e1' : '#f87171' }}>
          attempts {live?.strikesLeft ?? 0}/{live?.strikesGranted ?? 0}
        </span>
        {/* The gaps between the player's OWN picks, against the published band. Their arithmetic on their
            own choices — it says nothing about whether a stage sits in an alignment. */}
        <span style={{ ...row, fontSize: '0.74rem', color: 'var(--text-muted)' }}>
          {ats.length > 1
            ? ats.slice(1).map((t, i) => {
                const gap = t - ats[i];
                const good = gap >= (live?.recoverMs ?? 0) && gap <= (live?.patienceMs ?? 0);
                return (
                  <span key={i} style={{ color: good ? '#4ade80' : '#fbbf24', marginRight: '0.6rem' }}>
                    {i + 1}→{i + 2}: {gap}
                  </span>
                );
              })
            : 'one stage'}
        </span>
      </div>

      {/* Which stage is being placed. All buttons always mounted, so nothing shifts. */}
      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        {ats.map((t, i) => (
          <button
            key={i}
            onClick={() => setEditing(i)}
            style={{
              ...row, fontSize: '0.76rem', padding: '0.25rem 0.55rem', minWidth: '6.5rem',
              border: `1px solid ${i === editing ? 'var(--primary-color)' : '#232833'}`,
              color: i === editing ? '#e2e8f0' : '#9aa4b2',
            }}
          >
            stage {i + 1}: {t}
          </button>
        ))}
      </div>

      {/* The schedules across the whole timeline, plus every stage mark. */}
      <div style={{
        border: '1px solid #232833', borderRadius: 4, padding: '0.6rem 0.7rem',
        marginBottom: '0.6rem', background: '#0d1117',
      }}>
        {watchers.map(w => (
          <div key={w.name} style={{ marginBottom: '0.45rem' }}>
            <div style={{ ...row, fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.15rem' }}>
              {w.name} — blind {w.startMs}..{w.startMs + w.lenMs} ms of every {w.periodMs} ms
            </div>
            <div style={{ position: 'relative', height: 15, background: '#161b22', borderRadius: 2 }}>
              {Array.from({ length: Math.ceil(span / w.periodMs) }, (_, k) => {
                const from = k * w.periodMs + w.startMs;
                if (from >= span) return null;
                return (
                  <div key={k} style={{
                    position: 'absolute', top: 0, bottom: 0,
                    left: pct(from), width: pct(Math.min(w.lenMs, span - from)),
                    background: 'rgba(74,222,128,0.32)',
                  }} />
                );
              })}
              {/* One neutral mark per stage: no colour says whether it fits. */}
              {ats.map((t, i) => (
                <div key={i} style={{
                  position: 'absolute', top: -2, bottom: -2,
                  left: pct(t), width: pct(Math.max(write, span / 500)),
                  background: i === editing ? '#e2e8f0' : '#6b7280',
                }} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* The magnifier: the same rows around the stage being placed, at a scale the eye can judge. */}
      <div style={{
        border: '1px solid #232833', borderRadius: 4, padding: '0.6rem 0.7rem',
        marginBottom: '0.6rem', background: '#0d1117',
      }}>
        <div style={{ ...row, fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
          stage {editing + 1} close up · {Math.round(z0)} → {Math.round(z0 + ZOOM_MS)} ms
        </div>
        {watchers.map(w => (
          <div key={w.name} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
            <span style={{ ...row, fontSize: '0.7rem', minWidth: '5.2rem', color: 'var(--text-muted)' }}>
              {w.name}
            </span>
            <div style={{ position: 'relative', flex: 1, height: 18, background: '#161b22', borderRadius: 2 }}>
              {windowsIn(w, z0, z0 + ZOOM_MS).map((win, i) => (
                <div key={i} style={{
                  position: 'absolute', top: 0, bottom: 0,
                  left: zpct(win.from), width: `${((win.to - win.from) / ZOOM_MS) * 100}%`,
                  background: 'rgba(74,222,128,0.32)',
                }} />
              ))}
              <div style={{
                position: 'absolute', top: 3, bottom: 3,
                left: zpct(at), width: `${(write / ZOOM_MS) * 100}%`,
                background: '#e2e8f0', borderRadius: 1,
              }} />
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <input
          type="range"
          min={0}
          max={span - write}
          value={at}
          onChange={e => setAt(Number(e.target.value))}
          style={{ flex: '1 1 200px', minWidth: 160 }}
        />
        <input
          type="number"
          min={0}
          max={span - write}
          value={at}
          onChange={e => setAt(Number(e.target.value) || 0)}
          style={{ ...row, width: '6rem', padding: '0.2rem 0.4rem' }}
        />
        {[-10, -1, +1, +10].map(step => (
          <button
            key={step}
            onClick={() => setAt(at + step)}
            style={{ ...row, fontSize: '0.75rem', padding: '0.2rem 0.45rem', minWidth: '2.6rem' }}
          >
            {step > 0 ? `+${step}` : step}
          </button>
        ))}
      </div>

      {/* Reserved slot: a report must never move the buttons under it. */}
      <div style={{ minHeight: '4rem', marginBottom: '0.4rem' }}>
        {note && <div style={{ ...row, fontSize: '0.76rem', color: '#fbbf24' }}>{note}</div>}
        {!note && history.length > 0 && history[history.length - 1].notes.map((n, i) => (
          <div key={i} style={{
            ...row, fontSize: '0.75rem',
            color: n.includes('through at') ? '#4ade80' : '#f87171',
          }}>
            {n}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="primary"
          onClick={() => {
            if (ats.some((t, i) => i > 0 && t <= ats[i - 1])) {
              setNote('The stages have to be in order — move them apart.');
              return;
            }
            void send({ kind: 'strike', ats });
          }}
          style={{ fontSize: '0.85rem', padding: '0.35rem 0.9rem' }}
        >
          Run the chain
        </button>
        <button
          onClick={() => onSubmit({ ats })}
          style={{ ...row, fontSize: '0.76rem', padding: '0.25rem 0.6rem' }}
          title="Give up on this window"
        >
          Detach
        </button>
      </div>
    </div>
  );
}
